import { isEmpty, type Optional, stringUtils } from '@festina-life/core'
import isEqual from 'lodash-es/isEqual'
import { type JSX, useCallback, useEffect, useState } from 'react'

import type { Column, ColumnId } from './createColumnDefiner'

type Options<T> = {
  columns: Record<ColumnId, Column<T>>
  rows: Optional<T>[]
  globalSearch: string
}

type FilterState<T> = {
  getFilteredRows: (rows: T[]) => T[]
  getFilterState: (columnId: ColumnId) => ColumnFilterState<T> | null
  setFilterState: (columnId: ColumnId, state: ColumnFilterState<T>) => void
}

type ColumnFilterAutoOption<T> = {
  value: unknown
  label: ReturnType<Column<T>['getCellDisplay']>
  checked: boolean
}

export type ColumnFilterState<T> =
  | { type: 'auto'; options: ColumnFilterAutoOption<T>[] }
  | { type: 'numberRange'; min: number | null; max: number | null }
  | { type: 'dateRange'; min: string | null; max: string | null }
  | {
      type: 'radio'
      options: { label: string; filter: (datum: T) => boolean }[]
      selected: string
    }

type ColumnFilter<T> = {
  columnId: ColumnId
  state: ColumnFilterState<T>
}

const initializeFilterState = <T>(
  filterMode: Column<T>['filterMode'],
): ColumnFilterState<T> | null => {
  const mode =
    typeof filterMode === 'string' ? { type: filterMode } : filterMode

  switch (mode?.type) {
    case 'auto':
      return { type: 'auto', options: [] }
    case 'numberRange':
      return { type: 'numberRange', min: null, max: null }
    case 'dateRange':
      return { type: 'dateRange', min: null, max: null }
    case 'radio':
      return {
        type: 'radio',
        options: mode.options,
        selected: mode.options[0].label,
      }
    case undefined:
    default:
      return null
  }
}

const createFilter = <T>(
  column: Column<T>,
  state: ColumnFilterState<T>,
): ((row: T) => boolean) => {
  switch (state.type) {
    case 'auto':
      return (row) => {
        if (!state.options.length) {
          return true // if there are no options, don't filter anything out
        }
        const value = column.getCellValue(row)
        return Array.isArray(value)
          ? value.some(
              (item) =>
                state.options.find((option) => isEqual(option.value, item))
                  ?.checked !== false,
            )
          : state.options.find((option) => isEqual(option.value, value))
              ?.checked !== false
      }
    case 'numberRange':
      return (row) => {
        const value = column.getCellValue(row)
        if (typeof value === 'number') {
          return (
            (state.min === null || value >= state.min) &&
            (state.max === null || value <= state.max)
          )
        }
        if (Array.isArray(value)) {
          return (
            value.every((item) => typeof item !== 'number') ||
            value.some(
              (item) =>
                typeof item === 'number' &&
                (state.min === null || item >= state.min) &&
                (state.max === null || item <= state.max),
            )
          )
        }
        return true
      }
    case 'dateRange':
      return (row) => {
        const value = column.getCellValue(row)
        if (typeof value === 'string') {
          return (
            (state.min === null || value >= state.min) &&
            (state.max === null || value <= state.max)
          )
        }
        if (Array.isArray(value)) {
          return (
            value.every((item) => typeof item !== 'string') ||
            value.some(
              (item) =>
                typeof item === 'string' &&
                (state.min === null || item >= state.min) &&
                (state.max === null || item <= state.max),
            )
          )
        }
        return true
      }
    case 'radio': {
      const option = state.options.find(
        (option) => option.label === state.selected,
      )
      return option?.filter ?? (() => true)
    }
  }
}

function toSearchableString(value: unknown): string {
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'number') {
    return `${value}`
  }
  if (typeof value !== 'object') {
    return '' // booleans deliberately excluded as those are translation-dependant
  }
  // Best-effort at extracting strings from JSX with children.
  const children = (value as JSX.Element)?.props?.children
  return Array.isArray(children)
    ? children.map(toSearchableString).join(' ')
    : toSearchableString(children)
}

export const useFilterState = <T>({
  columns,
  rows,
  globalSearch,
}: Options<T>): FilterState<T> => {
  const [filters, setFilters] = useState<ColumnFilter<T>[]>(
    Object.entries(columns).flatMap(([columnId, column]) => {
      const state = initializeFilterState(column.filterMode)
      return state ? { columnId, state } : []
    }),
  )

  useEffect(() => {
    setFilters((filters) => {
      const newFilters = filters.flatMap<ColumnFilter<T>>((filter) => {
        const { columnId, state } = filter
        const column = columns[columnId]
        if (!column) {
          return []
        }
        if (state.type !== 'auto') {
          return filter
        }

        const options: ColumnFilterAutoOption<T>[] = []
        const pushOptionIfValidAndUnique = ({
          value,
          getLabel,
        }: {
          value: unknown
          getLabel: () => ColumnFilterAutoOption<T>['label']
        }) => {
          if (isEmpty(value)) {
            return // not valid
          }
          if (options.some((option) => isEqual(option.value, value))) {
            return // not unique
          }
          const label = getLabel()
          if (!label) {
            return // no point displaying empty labels
          }

          const checked =
            // new items should be checked by default, and old ones have their state respected
            state.options.find((option) => isEqual(option.value, value))
              ?.checked !== false

          options.push({
            value,
            label,
            checked,
          })
        }

        for (const row of rows) {
          if (!row) {
            continue
          }
          const value = column.getCellValue(row)
          if (Array.isArray(value)) {
            for (const item of value) {
              pushOptionIfValidAndUnique({
                value: item,
                getLabel: () => column.getValueDisplay(item),
              })
            }
          } else {
            pushOptionIfValidAndUnique({
              value,
              getLabel: () => column.getCellDisplay(row),
            })
          }
        }

        return { columnId, state: { type: 'auto', options } }
      })

      return newFilters.length === filters.length &&
        newFilters.every((filter, i) => filter === filters[i])
        ? filters
        : newFilters
    })
  }, [rows, columns])

  const getFilteredRows = useCallback<FilterState<T>['getFilteredRows']>(
    (rows) => {
      const filterFunctions = filters.flatMap(({ columnId, state }) => {
        const column = columns[columnId]
        return column ? createFilter(column, state) : []
      })
      if (globalSearch) {
        const columnsArray = Object.values(columns)
        const searchRegExp = new RegExp(
          stringUtils.escapeRegexp(globalSearch),
          'i',
        )
        filterFunctions.push((row) =>
          columnsArray.some((column) => {
            const display = column.getCellDisplay(row)
            const searchable = toSearchableString(display)
            return searchRegExp.test(searchable)
          }),
        )
      }

      return rows.filter((row) => filterFunctions.every((fn) => fn(row)))
    },
    [globalSearch, columns, filters],
  )

  const getFilterState = useCallback<FilterState<T>['getFilterState']>(
    (columnId) =>
      filters.find((filter) => filter.columnId === columnId)?.state ?? null,
    [filters],
  )

  const setFilterState = useCallback<FilterState<T>['setFilterState']>(
    (columnId, state) =>
      setFilters((filters) =>
        filters.map((filter) =>
          filter.columnId === columnId
            ? { columnId: filter.columnId, state }
            : filter,
        ),
      ),
    [],
  )

  return {
    getFilteredRows,
    getFilterState,
    setFilterState,
  }
}
